/**
 * Types for Clinical Notes Entity Verification System
 */

export type EntityCategory =
  | 'medications'
  | 'symptoms'
  | 'lab_values'
  | 'vital_signs'
  | 'procedures'
  | 'medical_history'
  | 'diagnoses'
  | 'demographics'
  | 'other';

export type EntityRelevance = 'high' | 'medium' | 'low';

export type AccuracyMatch = 'exact' | 'partial' | 'inaccurate' | 'not_present';

export type HallucinationClassification =
  | 'supported_inference'
  | 'potential_hallucination';

export type HallucinationSeverity = 'low' | 'medium' | 'high';

/**
 * A verified entity from the clinical note
 */
/** Issue type shown on cards (e.g. typo, inference). API may send this; otherwise derived from details. */
export type IssueTypeKey =
  | 'typo'
  | 'inference'
  | 'incorrectValue'
  | 'missing'
  | 'inaccurate'
  | 'supportedInference'
  | 'potentialHallucination';

export interface EntityVerification {
  input_entity: string;
  input_entity_exact_quotes: string[];
  output_entity: string;
  output_quotes: string[];
  category: EntityCategory;
  relevance: EntityRelevance;
  source: string;
  present_in_output: boolean;
  accuracy_match: AccuracyMatch;
  details: string;
  notes: string;
  /** Suggested replacement text to fix inaccurate output entities. */
  suggested_fix?: string;
  /** Optional: what's wrong (e.g. typo, inference). Used for card label when present. */
  issue_type?: string;
  /** Reference to entity_verification_feedback document ID */
  feedback_id?: string | null;
}

/**
 * Potential hallucination or unsupported inference
 */
export interface AdditionalInformation {
  output_entity: string;
  output_quotes: string[];
  category: EntityCategory;
  classification: HallucinationClassification;
  severity: HallucinationSeverity;
  justification: string;
  /** Suggested replacement text for the flagged content. Empty string means remove. */
  suggested_fix?: string;
  related_entities: number[];
  /** Reference to entity_verification_feedback document ID */
  feedback_id?: string | null;
}

/**
 * AN-ACC reassessment evidence flag detected in the clinical note.
 */
export interface AnAccFinding {
  domain: string;
  signal: string;
  implied_higher_need: boolean;
  severity: string;
  rationale: string;
  note_excerpt: string;
  suggested_action: string;
}

/**
 * Container for AN-ACC reassessment evidence flags.
 */
export interface AnAccFindingsData {
  findings: AnAccFinding[];
}

/**
 * Summary statistics for entity verification
 */
export interface VerificationSummary {
  total_entities_checked: number;
  entities_present: number;
  entities_missing: number;
  entities_high_relevance_missing: number;
  entities_medium_relevance_missing: number;
  entities_low_relevance_missing: number;
  entities_inaccurate: number;
  coverage_percentage: number;
  potential_hallucinations: number;
  potential_high_severity_hallucinations: number;
  potential_medium_severity_hallucinations: number;
  potential_low_severity_hallucinations: number;
  supported_inferences: number;
}

/**
 * Container for all entity verification results
 */
export interface EntityVerificationData {
  entity_verification: EntityVerification[];
  additional_information: AdditionalInformation[];
  verification_summary: VerificationSummary;
}

/**
 * Lifecycle status for a ClinicalNotesAnalysis.
 * Non-terminal statuses (all except COMPLETED / FAILED) are in-flight.
 */
export type AnalysisStatus =
  | 'pending'
  | 'extracting'
  | 'awaiting_note'
  | 'verifying'
  | 'completed'
  | 'failed';

/** Whether this analysis was triggered manually (Verify button) or automatically (auto mode). */
export type AnalysisSource = 'manual' | 'auto';

/** Statuses where an analysis is still in-flight and should be polled. */
export const IN_FLIGHT_ANALYSIS_STATUSES: readonly AnalysisStatus[] = [
  'pending',
  'extracting',
  'awaiting_note',
  'verifying',
] as const;

export const isAnalysisInFlight = (status: AnalysisStatus | undefined): boolean =>
  !!status && (IN_FLIGHT_ANALYSIS_STATUSES as readonly string[]).includes(status);

/**
 * Full clinical notes analysis document
 */
export interface ClinicalNotesAnalysis {
  id: string;
  session_id: string;
  note_id: string | null;
  user_id: string | null;
  clinical_note: string;
  entity_verification_data: EntityVerificationData;
  anacc_findings?: AnAccFindingsData | null;
  created_at: string;
  /**
   * Lifecycle status. Present on all analyses; defaults to 'completed' for older
   * documents created before the auto-mode migration.
   */
  status?: AnalysisStatus;
  /** Whether this analysis was triggered manually or automatically. */
  source?: AnalysisSource;
  /** When the pipeline began. */
  started_at?: string | null;
  /** When the pipeline reached a terminal state. */
  completed_at?: string | null;
  /** Short machine-readable reason when status is 'failed'. */
  failure_reason?: string | null;
  /** Note `updated_at` at the time the pipeline pinned the completed note.
   *  Compare against the live note's `updated_at` to detect staleness. */
  pinned_note_updated_at?: string | null;
}

/**
 * Response from POST /sessions/{id}/entity-verification/auto-start.
 * BE returns 202 Accepted with a pointer to the newly-created (or existing, if
 * one was already in-flight) analysis. Callers should poll GET
 * /sessions/{id}/entity-verification/ until `status` becomes terminal.
 */
export interface StartAutoVerificationResponse {
  analysis_id: string;
  session_id: string;
  status: AnalysisStatus;
  source: AnalysisSource;
}

/**
 * Step-level timing data from the analysis pipeline (milliseconds).
 */
export interface AnalysisTimings {
  extraction_ms: number;
  verification_ms: number;
  total_ms: number;
}

/**
 * Response from analyze note endpoint
 */
export interface AnalyzeNoteResponse {
  analysis: ClinicalNotesAnalysis;
  message: string;
  timings?: AnalysisTimings;
}

/**
 * Match information for text highlighting
 */
export interface TextMatch {
  start: number;
  end: number;
  text: string;
  type: 'entity' | 'hallucination';
  index: number;
  data: EntityVerification | AdditionalInformation;
}

// ============================================================================
// Entity Verification Feedback Types
// ============================================================================

export type EntityFeedbackSource = 'entity_verification' | 'additional_information';

/**
 * A feedback record for a single entity in an analysis
 */
export interface EntityVerificationFeedbackView {
  feedback_id: string;
  analysis_id: string;
  entity_source: EntityFeedbackSource;
  entity_index: number;
  analysis_marked_correct: boolean;
  qualitative_feedback: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Request to submit or update entity verification feedback
 */
export interface SubmitEntityFeedbackRequest {
  analysis_id: string;
  entity_source: EntityFeedbackSource;
  entity_index: number;
  analysis_marked_correct: boolean;
  qualitative_feedback?: string | null;
}

/**
 * Response from submitting entity verification feedback
 */
export interface SubmitEntityFeedbackResponse {
  feedback: EntityVerificationFeedbackView;
}

/**
 * Response from getting all entity verification feedback for a session
 */
export interface GetEntityFeedbackListResponse {
  feedbacks: EntityVerificationFeedbackView[];
}

// ============================================================================
// Overall Verification Feedback Types (session-level)
// ============================================================================

export type OverallFeedbackRating = 'thumb_up' | 'thumb_down';

/**
 * Request to submit overall feedback for a verification run
 */
export interface SubmitOverallFeedbackRequest {
  analysis_id: string;
  rating: OverallFeedbackRating;
  qualitative_feedback?: string;
}

/**
 * Response from submitting overall verification feedback
 */
export interface SubmitOverallFeedbackResponse {
  feedback: OverallFeedbackView;
}

/**
 * Overall feedback record for a verification run.
 * Uses the shared user_feedback collection with target_type=ENTITY_VERIFICATION.
 */
export interface OverallFeedbackView {
  id: string;
  session_id: string;
  analysis_id: string;
  rating: OverallFeedbackRating;
  qualitative_feedback: string | null;
  created_at: string;
}

/**
 * Response from getting overall feedback (null if none exists)
 */
export interface GetOverallFeedbackResponse {
  feedback: OverallFeedbackView | null;
}
